import React from 'react';
import './frame-builder.scss';
import {observer} from 'mobx-react';
import {RenderV2Object, Inches} from './render-v2-d';
import {Moulding} from './moulding/moulding';
import {InnerContent} from './inner-content/inner-content';
import {FrameBuilderEngravedPlate} from './engraved-plate/engraved-plate';
import {plateBottomRailInches} from './engraved-plate/bottom-rail';
import {withSingleDocOpening} from './engraved-plate/single-doc-opening';
import {CustomItem} from './custom-item/custom-item';
import {FrameOld} from '../../product/rendering/frame-old';
import {FrameNone} from '../../product/rendering/frame-none';
import {ProductCustomizationState} from '../../product/rendering/render.def';
import {FrameBuilderStore} from './frame-builder-store';

type Props = {
    pcs: ProductCustomizationState;
    frame: RenderV2Object;
};

export const FrameBuilder = observer(({frame, pcs}: Props) => {
    if (pcs.CustomizationMethodID === 0) return <FrameNone pcs={pcs} />;
    if (pcs.CustomizationMethodID === 1) return <FrameOld pcs={pcs} />;

    const opened = withSingleDocOpening(frame, pcs);
    const railBottom = plateBottomRailInches(opened.railBottom, pcs);
    const layoutFrame = railBottom === opened.railBottom ? opened : {...opened, railBottom: railBottom as Inches};
    const store = new FrameBuilderStore(layoutFrame);
    const {frameWidth, frameHeight, contentWidth, contentHeight, maxCols, inchToPx} = store;
    const uvStyles = pcs.is_uv_print && pcs.uv_print_active ? (pcs.css?.uv_line_styles || []).filter(ls => ls.visible && !!ls.text) : [];

    store.DPI = pcs.DPI;

    return (
        <div id="frame-builder-container">
            <div
                id="frame-builder"
                style={{
                    width: inchToPx(frameWidth),
                    height: inchToPx(frameHeight),
                    background: frame.mat1ImageURL ? '' : frame.mat1Color,
                    backgroundImage: frame.mat1ImageURL ? `url("${frame.mat1ImageURL}")` : '',
                }}
            >
                <Moulding
                    height={contentHeight}
                    width={contentWidth}
                    store={store}
                />

                <InnerContent
                    maxCols={maxCols}
                    store={store}
                    width={contentWidth}
                    height={contentHeight}
                />

                {/* Product-renderer plate. UV lines stay above it (z-index 2). */}
                <FrameBuilderEngravedPlate
                    pcs={pcs}
                    store={store}
                    centerInBottomRail={layoutFrame.railBottom !== frame.railBottom}
                />

                {uvStyles.map((lineStyle, idx) => (
                    <div
                        key={`uv-line-v2-${idx}`}
                        style={{
                            position: 'absolute',
                            left: lineStyle.left,
                            top: lineStyle.top,
                            width: lineStyle.width,
                            height: lineStyle.height,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            fontFamily: lineStyle.fontFamily,
                            fontSize: lineStyle.fontSize,
                            lineHeight: lineStyle.height,
                            textAlign: 'center',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none',
                            textShadow: '0 0 2px rgba(0, 0, 0, 0.55), 0 1px 2px rgba(0, 0, 0, 0.35)',
                            zIndex: 2,
                        }}
                    >
                        {lineStyle.text}
                    </div>
                ))}

                <CustomItem store={store} />
            </div>
        </div>
    );
});
